/**
 * Murphy Door Configurator - URL Parameter Parser
 *
 * PURPOSE: Parse and validate URL configuration parameters, then delegate
 * selection restoration to the cascade-auto-selection-coordinator via events.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Support (support-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 1 & 19)
 * - Async publishEvent() helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle for parse/validation operations (Pattern 3)
 * - Event consumer handler for bootstrap (Pattern 4)
 * - Event replay ready - subscribes at module load time (Pattern 5)
 * - Bootstrap runtime protection via bootstrapComplete flag (Pattern 6)
 * - PPD-First data access via conductor delegation (Patterns 7, 8)
 * - Data request via conductor delegation (Pattern 9)
 * - UI update events for error notifications (Pattern 10)
 * - Async event publishing with await (Pattern 11)
 * - Performance tracking with configurable thresholds (Patterns 12, 13)
 * - Try/Catch + Failed Events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - XSS prevention: validates all parsed data types (Pattern 17)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Event constant usage - CONSTANTS.EVENTS.* only (Pattern 19)
 * - Conductor single publication (Pattern 20)
 *
 * REPLACES: md-config-support-url-recovery-animator.js (DEPRECATED)
 *
 * INTEGRATION FLOW:
 *   1. Module loads -> subscribes to bootstrap event at load time
 *   2. Bootstrap complete (SYSTEM_STATE_FROM_PPD_READY)
 *   3. Detects ?config= URL parameter
 *   4. Parses base64-encoded JSON
 *   5. Validates each selection against PPD via conductor delegation
 *   6. Publishes BUSINESS_CASCADE_APPLY_URL_SELECTIONS event
 *   7. Cascade coordinator applies selections through existing logic
 *   8. Clears URL parameter after successful application
 *
 * @version 2.0.0
 * @author Murphy Door Configurator Team
 */
(function (window) {
  'use strict';

  // Namespace initialization
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // All event types from centralized CONSTANTS.EVENTS.* - zero magic strings
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    PARSE: 50, // 50ms for URL parsing
    VALIDATION: 100, // 100ms for PPD validation
    TOTAL_OPERATION: 200, // 200ms for entire parse + validate + publish
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // INITIAL URL LATCH (read ONCE, at module load)
  // ============================================================================
  // Captured before ANY configurator URL write: this parser clears ?config=
  // on handoff and the persistence manager rewrites the query on every
  // selection change, so a lazy Phase 2 module (the build-preset module, the
  // render hook in the image export manager) that reads window.location
  // later would see a URL that no longer says what the page was opened with.
  //   hadConfig    ?config= was present -> a restore is pending at bootstrap
  //   buildPreset  raw ?build_preset= value (UNVALIDATED -- the consumer runs
  //                CONSTANTS.BUILD_PRESET_HANDLE_REGEX before using it)
  //   renderJob    ?md_render_job=1 -> the read-only render hook is unlocked
  const INITIAL_URL_FLAGS = (function latchInitialUrlFlags() {
    try {
      const params = new URLSearchParams(window.location.search);
      return Object.freeze({
        hadConfig: !!params.get('config'),
        buildPreset: params.get(CONSTANTS.BUILD_PRESET_URL_PARAM) || null,
        renderJob: params.get(CONSTANTS.RENDER_JOB_URL_PARAM) === '1',
      });
    } catch (error) {
      return Object.freeze({
        hadConfig: false,
        buildPreset: null,
        renderJob: false,
      });
    }
  })();

  // ============================================================================
  // RESTORE STATUS LATCH (the shared "restore pending" latch)
  // ============================================================================
  // 'none'      no ?config= at load -- nothing to wait for
  // 'pending'   ?config= present, restore not finished
  // 'completed' the url_restoration cascade published COMPLETED
  // 'failed'    PARSE_FAILED / VALIDATION_FAILED / url_restoration FAILED
  // Terminal states never go back to 'pending'. Consumers:
  // md-config-support-build-preset.js (share-load waits before applying) and
  // imageExportManager.renderConfigurationImage (renders the restored door).
  const RESTORE_STATUS = Object.freeze({
    NONE: 'none',
    PENDING: 'pending',
    COMPLETED: 'completed',
    FAILED: 'failed',
  });
  let restoreStatus = INITIAL_URL_FLAGS.hadConfig
    ? RESTORE_STATUS.PENDING
    : RESTORE_STATUS.NONE;
  let restoreFailureReason = null;
  const restoreWaiters = [];

  function settleRestore(status, reason) {
    if (restoreStatus !== RESTORE_STATUS.PENDING) return;
    restoreStatus = status;
    restoreFailureReason = reason || null;
    const waiters = restoreWaiters.splice(0, restoreWaiters.length);
    waiters.forEach(function (notify) {
      try {
        notify();
      } catch (error) {
        isDev() &&
          console.error('[UrlParamParser] Restore waiter threw:', error);
      }
    });
  }

  function getRestoreSnapshot(settled) {
    return Object.freeze({
      settled: settled,
      status: restoreStatus,
      reason: restoreFailureReason,
    });
  }

  /**
   * Resolves once the ?config= restore reached a terminal state (or at once
   * when there was nothing to restore). Never rejects: on timeout it resolves
   * { settled:false, status:'pending' } and the caller decides.
   * @param {number} [timeoutMs=10000]
   * @returns {Promise<{settled:boolean,status:string,reason:string|null}>}
   */
  function waitForRestoreSettled(timeoutMs) {
    const limit = typeof timeoutMs === 'number' ? timeoutMs : 10000;
    if (restoreStatus !== RESTORE_STATUS.PENDING) {
      return Promise.resolve(getRestoreSnapshot(true));
    }
    return new Promise(function (resolve) {
      let done = false;
      let timer = null;
      const notify = function () {
        if (done) return;
        done = true;
        if (timer) clearTimeout(timer);
        resolve(getRestoreSnapshot(true));
      };
      restoreWaiters.push(notify);
      timer = setTimeout(function () {
        if (done) return;
        done = true;
        const i = restoreWaiters.indexOf(notify);
        if (i >= 0) restoreWaiters.splice(i, 1);
        resolve(getRestoreSnapshot(false));
      }, limit);
    });
  }

  if (CoreEventBus && restoreStatus === RESTORE_STATUS.PENDING) {
    cleanupHandlers.push(
      CoreEventBus.subscribe(EVENTS.SYSTEM_URL_PARAM_PARSE_FAILED, function (e) {
        settleRestore(RESTORE_STATUS.FAILED, (e && e.error) || 'parse_failed');
      })
    );
    cleanupHandlers.push(
      CoreEventBus.subscribe(EVENTS.SYSTEM_URL_PARAM_VALIDATION_FAILED, function () {
        settleRestore(RESTORE_STATUS.FAILED, 'validation_failed');
      })
    );
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
        function (e) {
          if (e && e.context === 'url_restoration') {
            settleRestore(RESTORE_STATUS.COMPLETED, null);
          }
        }
      )
    );
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        function (e) {
          if (e && e.context === 'url_restoration') {
            settleRestore(RESTORE_STATUS.FAILED, 'cascade_failed');
          }
        }
      )
    );
  }

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  /**
   * Centralized async event publishing with error handling.
   * CoreEventBus handles payload freezing (Pattern #18 DRY).
   *
   * @param {string} eventType - Event type constant from CONSTANTS.EVENTS
   * @param {Object} detail - Event payload data
   * @param {string} [context='operation'] - Operation context for debugging
   * @returns {Promise<boolean>} Success status
   */
  async function publishEvent(eventType, detail, context) {
    context = context || 'operation';

    if (!CoreEventBus?.publish) {
      isDev() &&
        console.warn(
          `[UrlParamParser] EventBus not available, cannot publish: ${eventType}`
        );
      return false;
    }

    try {
      const enrichedDetail = Object.assign({}, detail, {
        timestamp: detail.timestamp || Date.now(),
        source: detail.source || 'UrlParamParser',
      });

      const success = await CoreEventBus.publish(eventType, enrichedDetail);

      if (!success) {
        isDev() &&
          console.warn(
            `[UrlParamParser] Event publication returned false: ${eventType}`,
            { context }
          );
      }

      return success;
    } catch (error) {
      isDev() &&
        console.error(
          `[UrlParamParser] Failed to publish event ${eventType}:`,
          error
        );

      // Attempt to publish failure event (avoid recursion)
      if (eventType !== EVENTS.SYSTEM_URL_PARAM_PARSE_FAILED) {
        try {
          await CoreEventBus.publish(EVENTS.SYSTEM_URL_PARAM_PARSE_FAILED, {
            error: error.message,
            context,
            originalEvent: eventType,
            timestamp: Date.now(),
            source: 'UrlParamParser',
          });
        } catch (nestedError) {
          isDev() &&
            console.error(
              '[UrlParamParser] Failed to publish failure event:',
              nestedError
            );
        }
      }

      return false;
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  /**
   * Track operation performance and publish slow operation events.
   *
   * @param {string} operationType - Type of operation being tracked
   * @param {number} duration - Duration in milliseconds
   * @param {Object} [metadata={}] - Additional metadata
   */
  function trackPerformance(operationType, duration, metadata) {
    metadata = metadata || {};
    const threshold = PERFORMANCE_THRESHOLDS[operationType];

    if (threshold && duration > threshold) {
      publishEvent(
        EVENTS.PERFORMANCE_URL_PARAM_PARSE_SLOW,
        {
          operation: operationType,
          duration: parseFloat(duration.toFixed(2)),
          threshold,
          severity: duration > threshold * 2 ? 'critical' : 'warning',
        },
        'performance_tracking'
      );

      isDev() &&
        console.warn(
          `[UrlParamParser] PERFORMANCE: ${operationType} took ${duration.toFixed(2)}ms (threshold: ${threshold}ms)`
        );
    }
  }

  // ============================================================================
  // CORE: extractUrlConfig
  // ============================================================================
  /**
   * Extracts the ?config= parameter from the current URL.
   *
   * @returns {string|null} The raw config parameter value, or null
   */
  function extractUrlConfig() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('config') || null;
    } catch (error) {
      isDev() &&
        console.error('[UrlParamParser] Failed to read URL parameters:', error);
      return null;
    }
  }

  // ============================================================================
  // CORE: parseConfigParam
  // ============================================================================
  /**
   * Parses a base64-encoded JSON config parameter into a selections object.
   *
   * PATTERN 17: XSS Prevention - validates all parsed data types
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {string} configParam - The raw URL config parameter
   * @returns {Object|null} Parsed selections object, or null on failure
   */
  function parseConfigParam(configParam) {
    const METHOD_SIGNATURE = 'UrlParamParser.parseConfigParam';

    try {
      if (!configParam || typeof configParam !== 'string') {
        return null;
      }

      // Decode base64 directly - the configParam from URLSearchParams.get() is already URL-decoded.
      // IMPORTANT: Do NOT use decodeURIComponent() here - URLSearchParams.get() already handles
      // URL decoding automatically. Using decodeURIComponent would cause a double-decoding bug.
      const jsonString = atob(configParam);

      // Parse JSON
      const parsed = JSON.parse(jsonString);

      // PATTERN 17: XSS Prevention - validate structure
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        isDev() &&
          console.error(
            `[${METHOD_SIGNATURE}] Parsed config is not a valid object`
          );
        return null;
      }

      // Validate each selection entry for expected shape
      const sanitized = {};
      const entries = Object.entries(parsed);

      for (let i = 0; i < entries.length; i++) {
        const categoryId = entries[i][0];
        const selection = entries[i][1];

        // PATTERN 17: XSS Prevention - sanitize category ID (string only, no HTML)
        if (typeof categoryId !== 'string' || categoryId.length === 0) {
          isDev() &&
            console.warn(
              `[${METHOD_SIGNATURE}] Skipping invalid category ID:`,
              categoryId
            );
          continue;
        }

        // Validate selection is an object with expected fields
        if (!selection || typeof selection !== 'object') {
          isDev() &&
            console.warn(
              `[${METHOD_SIGNATURE}] Skipping invalid selection for category "${categoryId}"`
            );
          continue;
        }

        // Sanitize and normalize selection fields
        sanitized[categoryId] = {
          productId:
            selection.productId != null ? String(selection.productId) : null,
          variantId:
            selection.variantId != null ? String(selection.variantId) : null,
          categoryType:
            selection.categoryType != null
              ? String(selection.categoryType)
              : null,
          selectionPath:
            selection.selectionPath != null
              ? String(selection.selectionPath)
              : null,
        };
      }

      if (Object.keys(sanitized).length === 0) {
        isDev() &&
          console.error(
            `[${METHOD_SIGNATURE}] No valid selections found in parsed config`
          );
        return null;
      }

      return sanitized;
    } catch (error) {
      isDev() &&
        console.error(
          `[${METHOD_SIGNATURE}] Failed to parse config param:`,
          error
        );
      return null;
    }
  }

  // ============================================================================
  // CORE: validateSelectionsAgainstPPD
  // ============================================================================
  /**
   * Validates parsed selections against the perfectProductDictionary via
   * conductor delegation (PPD-First, CCP compliant).
   *
   * PATTERN 7: Perfect Dictionary Only
   * PATTERN 8: No Raw Data Access
   * PATTERN 9: Data Request via conductor delegation
   *
   * @param {Object} selections - Parsed selections object
   * @param {Object} conductor - The central conductor
   * @returns {Object} { valid: Array, invalid: Array, warnings: Array }
   */
  function validateSelectionsAgainstPPD(selections, conductor) {
    const METHOD_SIGNATURE = 'UrlParamParser.validateSelectionsAgainstPPD';
    const valid = [];
    const invalid = [];
    const warnings = [];

    try {
      if (!conductor) {
        isDev() &&
          console.error(
            `[${METHOD_SIGNATURE}] Conductor not available for validation`
          );
        return {
          valid: [],
          invalid: Object.keys(selections),
          warnings: ['Conductor not available'],
        };
      }

      const entries = Object.entries(selections);

      for (let i = 0; i < entries.length; i++) {
        const categoryId = entries[i][0];
        const selection = entries[i][1];

        try {
          // Validate variant exists in PPD via conductor delegation
          if (selection.variantId) {
            const product = conductor.getProductByVariantId(
              selection.variantId
            );

            if (!product) {
              isDev() &&
                console.warn(
                  `[${METHOD_SIGNATURE}] Variant ${selection.variantId} not found in PPD for category "${categoryId}"`
                );
              invalid.push({
                categoryId,
                reason: 'variant_not_found',
                variantId: selection.variantId,
              });
              continue;
            }

            // Resolve productId from PPD if not provided or mismatched
            const resolvedProductId = product.id || product.productId || null;
            if (resolvedProductId) {
              selection.productId = String(resolvedProductId);
            }

            // PPD-First categoryId validation: reject stale/renamed category keys
            // getCategoryClassification() checks ppd.categories.get(categoryId) — returns null if unknown
            const categoryData =
              conductor.getCategoryClassification(categoryId);
            if (!categoryData) {
              isDev() &&
                console.warn(
                  `[${METHOD_SIGNATURE}] Category "${categoryId}" not found in PPD — stale or renamed category key (variant ${selection.variantId} exists). Rejecting to prevent state pollution.`
                );
              invalid.push({
                categoryId,
                reason: 'category_not_in_ppd',
                variantId: selection.variantId,
              });
              continue;
            }

            valid.push({
              categoryId,
              productId: selection.productId,
              variantId: selection.variantId,
              categoryType: selection.categoryType,
              selectionPath: selection.selectionPath || 'variant',
            });
          } else if (selection.productId) {
            // Product-level selection (no variant) - validate product exists
            // Use conductor's product lookup if available
            if (typeof conductor.getProductById === 'function') {
              const productData = conductor.getProductById(selection.productId);
              if (!productData) {
                isDev() &&
                  console.warn(
                    `[${METHOD_SIGNATURE}] Product ${selection.productId} not found in PPD for category "${categoryId}"`
                  );
                invalid.push({
                  categoryId,
                  reason: 'product_not_found',
                  productId: selection.productId,
                });
                continue;
              }
            }

            // PPD-First categoryId validation: reject stale/renamed category keys
            const categoryData =
              conductor.getCategoryClassification(categoryId);
            if (!categoryData) {
              isDev() &&
                console.warn(
                  `[${METHOD_SIGNATURE}] Category "${categoryId}" not found in PPD — stale or renamed category key (productId ${selection.productId}). Rejecting to prevent state pollution.`
                );
              invalid.push({
                categoryId,
                reason: 'category_not_in_ppd',
                productId: selection.productId,
              });
              continue;
            }

            valid.push({
              categoryId,
              productId: selection.productId,
              variantId: selection.variantId,
              categoryType: selection.categoryType,
              selectionPath: selection.selectionPath || 'product',
            });
          } else {
            // Neither productId nor variantId - skip
            warnings.push(
              `Category "${categoryId}" has no productId or variantId`
            );
          }
        } catch (entryError) {
          isDev() &&
            console.error(
              `[${METHOD_SIGNATURE}] Error validating category "${categoryId}":`,
              entryError
            );
          invalid.push({
            categoryId,
            reason: 'validation_error',
            error: entryError.message,
          });
        }
      }
    } catch (error) {
      isDev() &&
        console.error(`[${METHOD_SIGNATURE}] Validation failed:`, error);
      return {
        valid: [],
        invalid: Object.keys(selections),
        warnings: [error.message],
      };
    }

    return { valid, invalid, warnings };
  }

  /**
   * Pure, non-mutating wrapper around validateSelectionsAgainstPPD for other
   * modules (md-config-support-build-preset.js). The internal validator
   * rewrites selection.productId in place; this copies each entry first so a
   * caller's snapshot is never touched.
   * @param {Object} selections - { categoryId: {productId, variantId, ...} }
   * @param {Object} conductor
   * @returns {{valid: Array, invalid: Array, warnings: Array}}
   */
  function validateSelectionsPure(selections, conductor) {
    const copy = {};
    if (selections && typeof selections === 'object') {
      Object.keys(selections).forEach(function (categoryId) {
        const entry = selections[categoryId];
        copy[categoryId] =
          entry && typeof entry === 'object' ? Object.assign({}, entry) : entry;
      });
    }
    return validateSelectionsAgainstPPD(copy, conductor);
  }

  /**
   * Apply order for a batch of validated selections: "Wood & Finish" (the
   * master material selector) goes LAST so it cascades DOWN after every
   * component is applied. Without this, a component with a specific finish
   * (e.g. Back Entablature Black) can cascade UP and override the intended
   * master finish. Stable for everything else. Non-mutating.
   * @param {Array<{categoryId:string}>} selections
   * @returns {Array}
   */
  function orderSelectionsForApply(selections) {
    return (Array.isArray(selections) ? selections : [])
      .slice()
      .sort(function (a, b) {
        var aIsFinishMaster = /wood|finish/i.test((a && a.categoryId) || '');
        var bIsFinishMaster = /wood|finish/i.test((b && b.categoryId) || '');
        if (aIsFinishMaster && !bIsFinishMaster) return 1;
        if (!aIsFinishMaster && bIsFinishMaster) return -1;
        return 0;
      });
  }

  // ============================================================================
  // CORE: processUrlParams (5-STAGE EVENT LIFECYCLE)
  // ============================================================================
  /**
   * Main orchestration function: detects, parses, validates, and publishes
   * URL configuration selections for cascade application.
   *
   * PATTERN 3: 5-Stage Event Lifecycle
   * PATTERN 6: Bootstrap Runtime Protection
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {Object} conductor - The central conductor
   */
  function processUrlParams(conductor) {
    const METHOD_SIGNATURE = 'UrlParamParser.processUrlParams';
    const totalStartTime = performance.now();

    // PATTERN 6: Bootstrap runtime protection
    if (!bootstrapComplete) {
      isDev() &&
        console.warn(
          `[UrlParamParser] processUrlParams called before bootstrap complete`
        );
      publishEvent(
        EVENTS.SYSTEM_URL_PARAM_PARSE_FAILED,
        {
          error: 'bootstrap_not_complete',
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
      return;
    }

    try {
      // ======================================================================
      // DETECTION: Check for URL config parameter
      // ======================================================================
      const configParam = extractUrlConfig();

      if (!configParam) {
        isDev() &&
          console.log(
            `[${METHOD_SIGNATURE}] No ?config= parameter found in URL`
          );
        return;
      }

      // Publish detection event
      publishEvent(
        EVENTS.SYSTEM_URL_PARAM_DETECTED,
        {
          hasConfig: true,
          configLength: configParam.length,
        },
        METHOD_SIGNATURE
      );

      isDev() &&
        console.log(
          `[${METHOD_SIGNATURE}] URL config parameter detected (${configParam.length} chars)`
        );

      // ======================================================================
      // STAGE 1: PARSE START
      // ======================================================================
      const parseStartTime = performance.now();

      publishEvent(
        EVENTS.SYSTEM_URL_PARAM_PARSE_START,
        {
          configLength: configParam.length,
        },
        METHOD_SIGNATURE
      );

      // ======================================================================
      // STAGE 2: PARSE OPERATION
      // ======================================================================
      const selections = parseConfigParam(configParam);

      const parseDuration = performance.now() - parseStartTime;

      trackPerformance('PARSE', parseDuration, {
        configLength: configParam.length,
      });

      if (!selections) {
        // STAGE 5: PARSE FAILED
        publishEvent(
          EVENTS.SYSTEM_URL_PARAM_PARSE_FAILED,
          {
            error: 'invalid_config_format',
            configLength: configParam.length,
            duration: parseFloat(parseDuration.toFixed(2)),
          },
          METHOD_SIGNATURE
        );

        showInvalidLinkAlert();
        clearUrlConfigParam();
        return;
      }

      // PARSE SUCCESS
      const selectionCount = Object.keys(selections).length;
      publishEvent(
        EVENTS.SYSTEM_URL_PARAM_PARSE_SUCCESS,
        {
          selectionCount,
          categories: Object.keys(selections),
          duration: parseFloat(parseDuration.toFixed(2)),
        },
        METHOD_SIGNATURE
      );

      isDev() &&
        console.log(
          `[${METHOD_SIGNATURE}] Parsed ${selectionCount} selections from URL`
        );

      // ======================================================================
      // STAGE 3: VALIDATION START
      // ======================================================================
      const validationStartTime = performance.now();

      publishEvent(
        EVENTS.SYSTEM_URL_PARAM_VALIDATION_START,
        {
          selectionCount,
        },
        METHOD_SIGNATURE
      );

      const validationResult = validateSelectionsAgainstPPD(
        selections,
        conductor
      );

      const validationDuration = performance.now() - validationStartTime;

      trackPerformance('VALIDATION', validationDuration, {
        validCount: validationResult.valid.length,
        invalidCount: validationResult.invalid.length,
      });

      // ======================================================================
      // STAGE 3b: VALIDATION RESULT
      // ======================================================================
      if (validationResult.valid.length === 0) {
        // ALL selections invalid
        publishEvent(
          EVENTS.SYSTEM_URL_PARAM_VALIDATION_FAILED,
          {
            invalidCount: validationResult.invalid.length,
            invalidSelections: validationResult.invalid,
            warnings: validationResult.warnings,
            duration: parseFloat(validationDuration.toFixed(2)),
          },
          METHOD_SIGNATURE
        );

        showInvalidLinkAlert();
        clearUrlConfigParam();
        return;
      }

      // Validation succeeded (at least some selections valid)
      publishEvent(
        EVENTS.SYSTEM_URL_PARAM_VALIDATION_SUCCESS,
        {
          validCount: validationResult.valid.length,
          invalidCount: validationResult.invalid.length,
          validCategories: validationResult.valid.map(function (s) {
            return s.categoryId;
          }),
          warnings: validationResult.warnings,
          duration: parseFloat(validationDuration.toFixed(2)),
        },
        METHOD_SIGNATURE
      );

      if (validationResult.invalid.length > 0) {
        isDev() &&
          console.warn(
            `[${METHOD_SIGNATURE}] ${validationResult.invalid.length} selections were invalid and will be skipped:`,
            validationResult.invalid
          );
      }

      // ======================================================================
      // STAGE 4: PUBLISH CASCADE APPLY EVENT (DEFERRED)
      // ======================================================================
      // IMPORTANT: Defer publication to the next event loop tick using setTimeout(0).
      // This is a CRITICAL fix for a race condition. Both this module (UrlParamParser)
      // and the consumer (CascadeAutoSelectionCoordinator) initialize based on the
      // same event (SYSTEM_STATE_FROM_PPD_READY). There is no guarantee that the coordinator
      // will have subscribed to BUSINESS_CASCADE_APPLY_URL_SELECTIONS before this module
      // publishes it. Deferring ensures all synchronous code for the current event tick
      // (including the coordinator's subscriptions) completes before this event is published.
      // Sort: "Wood & Finish" master selector goes LAST so it cascades DOWN after
      // all component selections are applied. Without this, a component with a specific
      // finish (e.g. Back Entablature Black) can cascade UP and override the intended
      // master finish when URL param selections are processed in object key order.
      const validSelections = orderSelectionsForApply(validationResult.valid);
      const invalidCount = validationResult.invalid.length;

      setTimeout(function () {
        isDev() &&
          console.log(
            `[${METHOD_SIGNATURE}] Publishing CASCADE_APPLY_URL_SELECTIONS with ${validSelections.length} valid selections (deferred)`
          );

        publishEvent(
          EVENTS.BUSINESS_CASCADE_APPLY_URL_SELECTIONS,
          {
            selections: validSelections,
            totalParsed: selectionCount,
            validCount: validSelections.length,
            invalidCount,
            context: 'url_restoration',
          },
          METHOD_SIGNATURE
        );
      }, 0);

      // ======================================================================
      // TOTAL OPERATION PERFORMANCE
      // ======================================================================
      const totalDuration = performance.now() - totalStartTime;

      trackPerformance('TOTAL_OPERATION', totalDuration, {
        selectionCount,
        validCount: validationResult.valid.length,
      });

      isDev() &&
        console.log(
          `[${METHOD_SIGNATURE}] URL param processing complete in ${totalDuration.toFixed(2)}ms`
        );

      // Clear URL parameter after successful handoff to cascade
      clearUrlConfigParam();
    } catch (error) {
      // STAGE 5: OVERALL FAILURE
      isDev() && console.error(`[${METHOD_SIGNATURE}] Failed:`, error);

      publishEvent(
        EVENTS.SYSTEM_URL_PARAM_PARSE_FAILED,
        {
          error: error.message,
          stack: error.stack,
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );

      showInvalidLinkAlert();
      clearUrlConfigParam();
    }
  }

  // ============================================================================
  // HELPER: showInvalidLinkAlert
  // ============================================================================
  /**
   * Shows a user-facing alert when URL config is invalid/expired.
   * Uses UI event system for CCP compliance.
   *
   * PATTERN 10: UI Update Events
   */
  function showInvalidLinkAlert() {
    try {
      // Publish UI error notification event (CCP compliant)
      publishEvent(
        EVENTS.UI_SHOW_ERROR_NOTIFICATION,
        {
          message:
            "We're sorry, but this link no longer works. The configuration may have been updated or is no longer available.",
          type: 'warning',
          duration: 8000,
          source: 'url_param_parser',
        },
        'showInvalidLinkAlert'
      );

      // Fallback: browser alert if UI system not available
      isDev() &&
        console.warn(
          '[UrlParamParser] Invalid URL config - showing user notification'
        );
    } catch (error) {
      // Ultimate fallback: simple alert
      isDev() &&
        console.error('[UrlParamParser] Error showing notification:', error);
      try {
        window.alert("We're sorry, but this link no longer works.");
      } catch (alertError) {
        // Silently fail - we tried our best
      }
    }
  }

  // ============================================================================
  // HELPER: clearUrlConfigParam
  // ============================================================================
  /**
   * Removes the ?config= parameter (and a stale ?build_preset= share handle,
   * which the build-preset module already latched via INITIAL_URL_FLAGS) from
   * the URL without page reload. ONE write, through replaceStateQuietly
   * (footgun: MageWorx APO re-parses the PDP on every history write).
   */
  function clearUrlConfigParam() {
    try {
      const url = new URL(window.location);
      url.searchParams.delete('config');
      url.searchParams.delete(CONSTANTS.BUILD_PRESET_URL_PARAM);
      const utils = window.MurphyDoorConfigurator?.utils;
      if (utils?.replaceStateQuietly) {
        utils.replaceStateQuietly({}, '', url);
      } else {
        window.history.replaceState({}, '', url);
      }

      isDev() && console.log('[UrlParamParser] URL config parameter cleared');
    } catch (error) {
      isDev() &&
        console.error(
          '[UrlParamParser] Failed to clear URL config parameter:',
          error
        );
    }
  }

  // ============================================================================
  // MODULE INITIALIZATION
  // ============================================================================
  /**
   * Initialize the URL parameter parser module.
   *
   * PATTERN 4: Event Consumer Handlers
   * PATTERN 16: Initialization Guard
   * PATTERN 15: Cleanup Arrays
   *
   * @param {Object} conductor - The central conductor for delegation
   * @returns {boolean} True if initialization succeeded
   */
  function initialize(conductor) {
    // PATTERN 16: Initialization Guard
    if (isInitialized) {
      isDev() && console.warn('[UrlParamParser] Already initialized, skipping');
      return false;
    }

    if (!conductor) {
      isDev() &&
        console.error(
          '[UrlParamParser] Cannot initialize: conductor is required'
        );
      return false;
    }

    try {
      // Mark as initialized
      isInitialized = true;

      // Process URL params immediately
      processUrlParams(conductor);

      // Publish module ready event
      publishEvent(
        EVENTS.SYSTEM_MODULE_READY,
        {
          module: 'UrlParamParser',
          subscriptionCount: cleanupHandlers.length,
          timestamp: Date.now(),
        },
        'initialize'
      );

      isDev() &&
        console.log(
          `[UrlParamParser] Initialized with ${cleanupHandlers.length} event subscriptions`
        );

      return true;
    } catch (error) {
      isDev() &&
        console.error('[UrlParamParser] Initialization failed:', error);

      publishEvent(
        EVENTS.SYSTEM_URL_PARAM_PARSE_FAILED,
        {
          error: error.message,
          stack: error.stack,
          source: 'UrlParamParser.initialize',
        },
        'initialize'
      );

      return false;
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================
  /**
   * Cleanup all event subscriptions and reset module state.
   * PATTERN 15: Cleanup Arrays
   */
  function cleanup() {
    isDev() &&
      console.log(
        `[UrlParamParser] Cleaning up ${cleanupHandlers.length} subscriptions`
      );

    // Unsubscribe all event handlers
    cleanupHandlers.forEach(function (unsubscribe) {
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch (error) {
        isDev() &&
          console.error('[UrlParamParser] Cleanup handler error:', error);
      }
    });
    cleanupHandlers = [];

    // Reset state
    isInitialized = false;
    bootstrapComplete = false;
  }

  // ============================================================================
  // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
  // PUBLIC API (Exposed on namespace)
  // ============================================================================
  window.MurphyDoorConfigurator.supportUrlParamParser = {
    /**
     * Initialize the parser with conductor reference.
     * @param {Object} conductor - The central conductor
     * @returns {boolean} Success
     */
    initialize,

    /**
     * Cleanup all subscriptions and reset state.
     */
    cleanup,

    /**
     * Get current module state for debugging.
     * PATTERN 18: Returns frozen state object.
     * @returns {Object} Current module state (frozen)
     */
    getState() {
      return Object.freeze({
        isInitialized,
        bootstrapComplete,
        subscriptionCount: cleanupHandlers.length,
      });
    },

    // Expose configuration for external reference/testing
    PERFORMANCE_THRESHOLDS,
  };

  // Read-only facade for other Phase 2 modules (build-preset, render hook).
  // Pure functions and latched state only: nothing here mutates the URL or
  // publishes.
  window.MurphyDoorConfigurator.urlParamParser = Object.freeze({
    validateSelectionsAgainstPPD: validateSelectionsPure,
    orderSelectionsForApply,
    getInitialUrlFlags() {
      return INITIAL_URL_FLAGS;
    },
    getRestoreStatus() {
      return getRestoreSnapshot(restoreStatus !== RESTORE_STATUS.PENDING);
    },
    isRestorePending() {
      return restoreStatus === RESTORE_STATUS.PENDING;
    },
    waitForRestoreSettled,
    RESTORE_STATUS,
  });

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 5: EVENT REPLAY READY (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  /**
   * Subscribe to SYSTEM_STATE_FROM_PPD_READY at module load time to ensure
   * conductor and PPD are fully ready before initializing.
   *
   * FIX: Previously subscribed to SYSTEM_MAPS_INITIALIZED which fires BEFORE this
   * module loads (race condition). SYSTEM_STATE_FROM_PPD_READY is the correct
   * bootstrap completion event that all Phase 2 modules use (golden standard pattern).
   */
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      function () {
        isDev() &&
          console.log('[UrlParamParser] Bootstrap complete event received');
        bootstrapComplete = true;

        const conductor =
          window.MurphyDoorConfigurator.conductor ||
          window.MurphyDoorConfigurator.main;

        if (!conductor) {
          isDev() &&
            console.error(
              '[UrlParamParser] Conductor not available when SYSTEM_STATE_FROM_PPD_READY event fired'
            );
          return;
        }

        // C2: in a curated/dual experience the curated-defaults-coordinator
        // seeds a default door on this same bootstrap tick. Defer URL restore
        // until those defaults are applied so ?config= selections merge OVER
        // the defaults (partial shared links land 100% purchasable) instead of
        // racing them. Handle every ordering: already-applied (synchronous),
        // event-driven, and a safety timeout so we never hang.
        const experience =
          typeof conductor._getConfiguratorExperience === 'function'
            ? conductor._getConfiguratorExperience()
            : 'classic';
        const curatedMode =
          experience === 'dual_curated_default' ||
          experience === 'dual_full_default';

        const runInit = function () {
          if (isInitialized) return;
          const success = initialize(conductor);
          if (success) {
            isDev() &&
              console.log(
                '[UrlParamParser] Successfully initialized via system:state_from_ppd_ready'
              );
          }
        };

        if (curatedMode) {
          if (window.MurphyDoorConfigurator._curatedDefaultsApplied) {
            // Defaults already applied synchronously — restore over them now.
            runInit();
          } else {
            let ran = false;
            const once = function () {
              if (ran) return;
              ran = true;
              runInit();
            };
            const unsubCurated = CoreEventBus.subscribe(
              CONSTANTS.EVENTS.BUSINESS_CURATED_DEFAULTS_APPLIED,
              once
            );
            cleanupHandlers.push(unsubCurated);
            // Safety net: if curated defaults never signal (misconfig), restore
            // anyway so a shared config link is never lost.
            setTimeout(once, 3000);
          }
        } else {
          runInit();
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    isDev() &&
      console.log(
        '[UrlParamParser] Subscribed to bootstrap event at module load time'
      );
  }

  isDev() && console.log('[UrlParamParser] Module loaded');
})(window);
